import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFetch } from '@/hooks/use-fetch';
import { useWorkspace } from '@/contexts/workspace-context';
import { qk } from '@/lib/query/keys';
import { parseBody } from '@/lib/query/parse-body';
import { throwForResponse } from '@/lib/query/throw-for-response';
import { logger } from '@/lib/frontend-logger';
import type { DeliveryLog } from '@/types/delivery';

/**
 * Dev-only demo rows use `demo-`-prefixed ids and are seeded locally — they
 * must never hit the network (a real fetch would 404).
 */
export function isDemoId(id: string): boolean {
  return id.startsWith('demo-');
}

export interface DeliveryDetailsStore {
  /** Resolved delivery detail keyed by delivery id. */
  detailById: Record<string, DeliveryLog>;
  /**
   * Fetch one delivery's detail at most once for the panel's lifetime. The
   * id is recorded as requested *synchronously, before* the network call
   * starts, so overlapping renders / re-running effects can never issue a
   * duplicate request for it. Demo ids and already-requested ids are no-ops.
   * A failed fetch is logged (not silently swallowed) and the id stays
   * recorded, so automatic prefetch effects do not re-loop on it.
   */
  ensureDetail: (id: string) => void;
  /**
   * Re-issue a fetch even when the id was already requested — the escape
   * hatch for explicit user actions (expanding a row) so a failed automatic
   * prefetch is retryable on demand.
   */
  refetchDetail: (id: string) => void;
  /** Seed detail synchronously, without a network call (dev-only demo rows). */
  seedDetails: (seed: Record<string, DeliveryLog>) => void;
}

/**
 * Centralized delivery-detail cache for DeliveriesPanel.
 *
 * Phase 131 fix (2026-05-20 incident): the panel previously fetched
 * `/deliveries/:id` from three separate effect/handler loops, each guarded
 * only by the *resolved* cache (`detailById`). While a fetch was in flight —
 * and permanently, once it failed — the guard stayed false, so any re-render
 * of those effects re-issued the request. With the fetched state also
 * feeding the effects' dependency arrays, that compounded into an O(N²)+
 * request storm (~2000 requests for 55 deliveries).
 *
 * This hook makes "fetch a detail" idempotent: a `requestedRef` Set, written
 * before the await, bounds every id to one automatic request. The fetch
 * itself now goes through the TanStack Query cache (immutable rows, session
 * staleTime), so concurrent same-id fetches dedupe and a panel remount reads
 * from cache instead of the network — but the storm guard stays requestedRef,
 * NOT the query cache: a failed query must never be auto-retried by the
 * render-driven prefetch effects.
 */
export function useDeliveryDetails(): DeliveryDetailsStore {
  const fetchAuth = useFetch();
  const qc = useQueryClient();
  const { activeWorkspace } = useWorkspace();
  const wsId = activeWorkspace?.id ?? '';
  const [detailById, setDetailById] = useState<Record<string, DeliveryLog>>({});
  // Ids whose detail fetch has been issued — in flight, resolved, or failed.
  // Never cleared on failure: that is what bounds automatic prefetch to one
  // request per id and stops the render-driven effects from looping.
  const requestedRef = useRef<Set<string>>(new Set());

  const runFetch = useCallback(
    (id: string, source: 'auto' | 'manual') => {
      requestedRef.current.add(id); // record BEFORE the await — concurrent-render safe
      void qc
        .fetchQuery({
          queryKey: qk.workspace(wsId, 'delivery', id),
          queryFn: async () => {
            const res = await fetchAuth(`/deliveries/${id}`);
            if (!res.ok) await throwForResponse(res, `/deliveries/${id}`);
            return parseBody<DeliveryLog>(res);
          },
          // Delivery details are immutable rows — keep them fresh for the app
          // session so a remounted panel re-reads from cache, not the network.
          staleTime: Infinity,
        })
        .then((body) => {
          setDetailById((prev) => (prev[id] ? prev : { ...prev, [id]: body }));
        })
        .catch((err: unknown) => {
          // A swallowed failure here is what made the prefetch storm hard to
          // prove. Log it low-noise; the id stays in requestedRef so the
          // automatic effects never retry — refetchDetail() is the manual path.
          // `source` keeps an automatic prefetch failure queryable apart from
          // a user-triggered row-expand failure.
          logger.warn('deliveries.detail_fetch_failed', {
            deliveryId: id,
            source,
            error: err instanceof Error ? err.message : String(err),
          });
        });
    },
    [qc, fetchAuth, wsId],
  );

  const ensureDetail = useCallback(
    (id: string) => {
      if (isDemoId(id)) return;
      if (requestedRef.current.has(id)) return;
      runFetch(id, 'auto');
    },
    [runFetch],
  );

  const refetchDetail = useCallback(
    (id: string) => {
      if (isDemoId(id)) return;
      runFetch(id, 'manual');
    },
    [runFetch],
  );

  const seedDetails = useCallback((seed: Record<string, DeliveryLog>) => {
    for (const id of Object.keys(seed)) requestedRef.current.add(id);
    setDetailById((prev) => ({ ...seed, ...prev }));
  }, []);

  return { detailById, ensureDetail, refetchDetail, seedDetails };
}
